'use client';

import { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useAuth } from '@/lib/contexts/auth-context';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Menu, LogOut, Link2 as Link2Icon, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { usePermissions } from '@/lib/hooks/use-permissions';
import { visibleForRole } from '@/lib/permissions';
import { FOOTER_NAV, MORE_NAV, PRIMARY_NAV, isNavItemActive } from '@/lib/navigation';

const MOBILE_SECTIONS = [
  { label: null, items: PRIMARY_NAV },
  { label: 'More', items: MORE_NAV },
  { label: null, items: FOOTER_NAV },
];

export function Header() {
  const { user, logout } = useAuth();
  const pathname = usePathname();
  const { role } = usePermissions();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const getInitials = (email: string) => {
    return email.substring(0, 2).toUpperCase();
  };

  return (
    <header className="flex h-16 items-center justify-between border-b px-4 sm:px-6">
      <div className="flex items-center gap-2 sm:gap-4 min-w-0">
        {/* Mobile menu */}
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="md:hidden">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-64 p-0">
            <SheetHeader className="border-b p-6">
              <SheetTitle className="flex items-center space-x-2">
                <Link2Icon className="h-6 w-6" />
                <span className="text-xl font-bold">rii.link</span>
              </SheetTitle>
            </SheetHeader>
            <nav className="flex-1 space-y-4 overflow-y-auto p-4">
              {MOBILE_SECTIONS.map((section, i) => (
                <div key={i} className={cn('space-y-1', i > 0 && 'border-t pt-4')}>
                  {section.label && (
                    <p className="px-4 pb-1 text-xs font-semibold tracking-wider text-muted-foreground uppercase">
                      {section.label}
                    </p>
                  )}
                  {visibleForRole(section.items, role).map((item) => {
                    const isActive = isNavItemActive(pathname, item.href);
                    return (
                      <Link
                        key={item.name}
                        href={item.href}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Button
                          variant={isActive ? 'secondary' : 'ghost'}
                          className={cn('w-full justify-start', isActive && 'bg-secondary')}
                        >
                          <item.icon className="mr-3 h-5 w-5" />
                          {item.name}
                        </Button>
                      </Link>
                    );
                  })}
                </div>
              ))}
            </nav>
            <div className="border-t p-4">
              <Button
                variant="ghost"
                className="w-full justify-start text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={() => {
                  setMobileMenuOpen(false);
                  logout();
                }}
              >
                <LogOut className="mr-3 h-5 w-5" />
                Logout
              </Button>
            </div>
          </SheetContent>
        </Sheet>

      </div>

      {/* Empty stretch the header-docked toaster measures itself against (components/ui/sonner.tsx).
          From md up the menu button is hidden and the left group is empty, so the dock starts
          right at the header's padding (sm:px-6), the same x as main's content (sm:px-6):
          docked toasts line up with the page. Below md it keeps a gap after the menu button. */}
      <div data-toast-dock aria-hidden="true" className="ml-3 mr-3 h-full min-w-0 flex-1 md:ml-0" />

      <div className="flex items-center gap-2 sm:gap-4">
        {user && !user.is_verified && (
          <Badge variant="outline" className="text-yellow-600 border-yellow-600 hidden sm:flex">
            Email not verified
          </Badge>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="flex items-center gap-2 sm:gap-3 h-auto p-2">
              <div className="text-right hidden lg:block">
                <p className="text-sm font-medium">{user?.email}</p>
              </div>
              <Avatar>
                <AvatarFallback>{user?.email ? getInitials(user.email) : 'U'}</AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">{user?.email}</p>
                <p className="text-xs leading-none text-muted-foreground">
                  {user?.is_verified ? 'Verified account' : 'Email not verified'}
                </p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link href="/profile" className="cursor-pointer">
                <User className="mr-2 h-4 w-4" />
                Profile
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} className="text-destructive focus:text-destructive cursor-pointer">
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
